import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { ROLES, STATUSES } from '../constants/roles';
import { db } from '../firebase';

/**
 * Migration script to update existing users to the new status system
 * This should be run once to migrate existing users before public deployment
 */
export const migrateExistingUsers = async () => {
  const results = {
    processed: 0,
    updated: 0,
    errors: 0,
    details: []
  };

  try {
    console.log('🚀 Starting migration of existing users...');
    
    // Get all users
    const usersSnapshot = await getDocs(collection(db, 'users'));
    results.processed = usersSnapshot.size;
    
    console.log(`📊 Found ${results.processed} users to process`);

    // Process each user
    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      try {
        // Check if user already has status field
        if (userData.status) {
          results.details.push({
            id: userId,
            email: userData.email,
            action: 'skipped',
            reason: 'Already has status field'
          });
          continue;
        }

        // Determine migration strategy
        const updates = {};
        let migrationReason = '';

        // Add status field - existing users are automatically approved
        updates.status = STATUSES.ACTIVE;
        updates.isActive = true;
        migrationReason = 'Migrated existing user to active status';

        // Ensure role field is properly set
        if (!userData.role) {
          // Default to student if no role specified
          updates.role = ROLES.STUDENT;
          migrationReason += ', set default role to student';
        }

        // Add missing fields
        if (!userData.displayName && (userData.firstName || userData.lastName)) {
          updates.displayName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
          migrationReason += ', added displayName';
        }

        if (!userData.createdAt) {
          updates.migratedAt = serverTimestamp();
          migrationReason += ', added migratedAt timestamp';
        }

        if (!userData.schoolId) {
          updates.schoolId = null;
        }

        // Update the user document
        await updateDoc(doc(db, 'users', userId), updates);
        
        results.updated++;
        results.details.push({
          id: userId,
          email: userData.email,
          role: userData.role || updates.role,
          action: 'updated',
          reason: migrationReason
        });

        console.log(`✅ Updated user: ${userData.email}`);

      } catch (error) {
        results.errors++;
        results.details.push({
          id: userId,
          email: userData.email || 'unknown',
          action: 'error',
          reason: error.message
        });
        console.error(`❌ Error updating user ${userId}:`, error);
      }
    }

    console.log('✨ Migration completed!');
    console.log(`📊 Results:`);
    console.log(`   - Processed: ${results.processed}`);
    console.log(`   - Updated: ${results.updated}`);
    console.log(`   - Errors: ${results.errors}`);
    console.log(`   - Skipped: ${results.processed - results.updated - results.errors}`);

    return results;

  } catch (error) {
    console.error('💥 Migration failed:', error);
    throw error;
  }
};

/**
 * Dry run migration - shows what would be changed without making changes
 */
export const dryRunMigration = async () => {
  const results = {
    wouldProcess: 0,
    wouldUpdate: 0,
    wouldSkip: 0,
    details: []
  };

  try {
    console.log('🔍 Running dry run migration...');
    
    const usersSnapshot = await getDocs(collection(db, 'users'));
    results.wouldProcess = usersSnapshot.size;
    
    console.log(`📊 Found ${results.wouldProcess} users to analyze`);

    for (const userDoc of usersSnapshot.docs) {
      const userId = userDoc.id;
      const userData = userDoc.data();
      
      if (userData.status) {
        results.wouldSkip++;
        results.details.push({
          id: userId,
          email: userData.email,
          currentRole: userData.role,
          currentStatus: userData.status,
          action: 'would skip',
          reason: 'Already has status field'
        });
      } else {
        results.wouldUpdate++;
        const proposedUpdates = {
          status: STATUSES.ACTIVE,
          isActive: true,
          role: userData.role || ROLES.STUDENT
        };
        
        results.details.push({
          id: userId,
          email: userData.email,
          currentRole: userData.role,
          currentStatus: userData.status || 'none',
          action: 'would update',
          proposedUpdates: proposedUpdates
        });
      }
    }

    console.log('🔍 Dry run completed!');
    console.log(`📊 Results:`);
    console.log(`   - Would process: ${results.wouldProcess}`);
    console.log(`   - Would update: ${results.wouldUpdate}`);
    console.log(`   - Would skip: ${results.wouldSkip}`);

    return results;

  } catch (error) {
    console.error('💥 Dry run failed:', error);
    throw error;
  }
};

/**
 * Utility to check if migration is needed
 */
export const checkMigrationStatus = async () => {
  
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    let usersWithoutStatus = 0;
    let totalUsers = usersSnapshot.size;
    
    usersSnapshot.forEach(doc => {
      const userData = doc.data();
      if (!userData.status) {
        usersWithoutStatus++;
      }
    });
    
    const migrationNeeded = usersWithoutStatus > 0;
    
    console.log(`📊 Migration Status Check:`);
    console.log(`   - Total users: ${totalUsers}`);
    console.log(`   - Users without status: ${usersWithoutStatus}`);
    console.log(`   - Migration needed: ${migrationNeeded ? 'YES' : 'NO'}`);
    
    return {
      totalUsers,
      usersWithoutStatus,
      migrationNeeded
    };
    
  } catch (error) {
    console.error('💥 Status check failed:', error);
    throw error;
  }
};